import { Suspense, useState } from 'react'
import type { FormEvent } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useFetchUncachedCb } from '@gluedigital/ruse-fetch-extras'
import { useDialog } from '@hooks/useDialog'
import { useUserAcl } from '@hooks/useUserAcl'
import ErrorBoundary from '@components/ErrorBoundary/ErrorBoundary'
import TagsInput from '@components/TagsInput/TagsInput'
import Toast from '@components/Toast/Toast'
import { ADMIN_URL } from 'src/constants/global'
import type { User } from 'src/types/global'

import './UserForm.css'

interface UserFormProps {
  onClose: () => void
  user?: User
  companyId: string
  companyName: string
  onSaved: (user: User) => void
}

interface UserResponse {
  ok: boolean
  user: User
  error?: string
}

const ACL_INPUT_ID = 'user-form-acl'

interface AclFieldProps {
  values: string[]
  onChange: (values: string[]) => void
  placeholder: string
}

const AclFieldFallback = ({ values, onChange, placeholder }: AclFieldProps) => (
  <TagsInput
    id={ACL_INPUT_ID}
    values={values}
    onChange={onChange}
    placeholder={placeholder}
  />
)

const AclFieldWithSuggestions = (props: AclFieldProps) => {
  const suggestions = useUserAcl()
  return <TagsInput id={ACL_INPUT_ID} {...props} suggestions={suggestions} />
}

export default function UserForm({
  onClose,
  user,
  companyId,
  companyName,
  onSaved
}: UserFormProps) {
  const intl = useIntl()
  const { dialogRef } = useDialog(true, onClose)
  const doSubmit = useFetchUncachedCb()
  const isEdit = !!user

  const [email, setEmail] = useState(user?.email ?? '')
  const [name, setName] = useState(user?.name ?? '')
  const [acl, setAcl] = useState<string[]>(user?.acl ?? [])
  const [editReason, setEditReason] = useState('')
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!email.trim() || !name.trim()) return
    if (isEdit && !editReason.trim()) return

    setIsSubmitting(true)
    setError('')
    try {
      const response = await doSubmit<UserResponse>(
        isEdit ? `${ADMIN_URL}/users/${user.id}` : `${ADMIN_URL}/users`,
        {
          method: isEdit ? 'PUT' : 'POST',
          body: isEdit
            ? { email, name, acl, reason: editReason.trim() }
            : { email, name, company_id: companyId, acl }
        }
      )
      onSaved(response.user)
      onClose()
    } catch (err) {
      const payload = (err as { payload?: { error?: string } })?.payload
      setError(
        payload?.error || intl.formatMessage({ id: 'user-form.error.generic' })
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const aclPlaceholder = intl.formatMessage({ id: 'user-form.acl-placeholder' })

  return (
    <dialog
      id="user-form-dialog"
      className="app-dialog"
      ref={dialogRef}
      onCancel={onClose}
    >
      <form className="dialog-inner" onSubmit={handleSubmit}>
        <h3>
          <FormattedMessage
            id={isEdit ? 'user-form.title.edit' : 'user-form.title.create'}
          />
        </h3>
        <label htmlFor="user-form-email">
          <strong>
            <FormattedMessage id="user-form.email" />
          </strong>
          <input
            id="user-form-email"
            required
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <label htmlFor="user-form-name">
          <strong>
            <FormattedMessage id="user-form.name" />
          </strong>
          <input
            id="user-form-name"
            required
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label htmlFor="user-form-company">
          <strong>
            <FormattedMessage id="user-form.company" />
          </strong>
          <input
            id="user-form-company"
            type="text"
            disabled
            value={companyName}
          />
        </label>
        <label htmlFor={ACL_INPUT_ID}>
          <strong>
            <FormattedMessage id="user-form.acl" />
          </strong>
          <ErrorBoundary
            errorComponent={() => (
              <AclFieldFallback
                values={acl}
                onChange={setAcl}
                placeholder={aclPlaceholder}
              />
            )}
          >
            <Suspense
              fallback={
                <AclFieldFallback
                  values={acl}
                  onChange={setAcl}
                  placeholder={aclPlaceholder}
                />
              }
            >
              <AclFieldWithSuggestions
                values={acl}
                onChange={setAcl}
                placeholder={aclPlaceholder}
              />
            </Suspense>
          </ErrorBoundary>
        </label>
        {isEdit && (
          <label htmlFor="user-form-edit-reason">
            <strong>
              <FormattedMessage id="user-form.edit-reason" />
            </strong>
            <textarea
              id="user-form-edit-reason"
              rows={3}
              required
              value={editReason}
              disabled={isSubmitting}
              placeholder={intl.formatMessage({
                id: 'user-form.edit-reason-placeholder'
              })}
              onChange={(e) => setEditReason(e.target.value)}
            />
          </label>
        )}
        <div className="dialog-actions">
          <button type="button" onClick={onClose} disabled={isSubmitting}>
            <FormattedMessage id="cancel" />
          </button>
          <button
            type="submit"
            className="highlight-button"
            disabled={isSubmitting}
          >
            <FormattedMessage id="save" />
          </button>
        </div>
      </form>
      {error && <Toast message={error} onClose={() => setError('')} />}
    </dialog>
  )
}
